import { isInactiveInvoice } from "./invoiceVisibility";

const DEPOSIT_INVOICE_ROLES = new Set([
  "deposit",
  "deposit-invoice",
  "deposit_request",
  "depositrequest",
]);

const parseMaybeJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

export const getInvoiceRole = (invoice) => {
  const relationships = parseMaybeJsonObject(invoice?.invoiceRelationships);
  return String(
    relationships?.role || relationships?.type || invoice?.invoiceType || "",
  )
    .trim()
    .toLowerCase();
};

export const isDepositInvoiceRole = (invoice) =>
  DEPOSIT_INVOICE_ROLES.has(getInvoiceRole(invoice));

/**
 * Display-only allocation of a deposit invoice's *existing* gross amount.
 *
 * Deposit invoices are immutable financial records once paid. The historical
 * invoice amount (and recorded payment) wins over a prorated pre-tax estimate.
 * Allocate displayed tax in whole cents, then give the residual cents to the
 * deposit's pre-tax line so the displayed parts always add to the saved gross.
 * Never change the invoice, checkout amount, tax snapshot, or payment ledger.
 */
export const getDepositInvoiceDisplayAllocation = ({
  amountCents,
  proratedTaxRows = [],
  fallbackTaxCents = 0,
} = {}) => {
  const gross = Math.max(0, Math.round(Number(amountCents) || 0));
  const rows = Array.isArray(proratedTaxRows) ? proratedTaxRows : [];
  const requestedTax = rows.length
    ? rows.reduce((sum, row) => sum + Math.round(Number(row?.taxCents) || 0), 0)
    : Math.round(Number(fallbackTaxCents) || 0);
  const taxCents = Math.min(gross, Math.max(0, requestedTax));
  return {
    grossCents: gross,
    subtotalCents: gross - taxCents,
    taxCents,
  };
};

export const getInvoiceDocumentIdentity = (invoice) => {
  const isDeposit = isDepositInvoiceRole(invoice);
  return {
    title: isDeposit ? "Deposit Invoice" : "Invoice",
    numberLabel: isDeposit ? "Deposit Invoice #" : "Invoice #",
    numberPrefix: isDeposit ? "DEP" : "INV",
  };
};

export const filterInvoicesForFinanceView = (invoices = [], view = "invoices") =>
  (Array.isArray(invoices) ? invoices : []).filter((invoice) => {
    // Retired/superseded records remain available in project invoice history,
    // but must not be presented as actionable finance invoices.
    // Paid invoices with a legacy isActive=false flag remain visible.
    if (isInactiveInvoice(invoice)) return false;

    const isDeposit = isDepositInvoiceRole(invoice);
    return view === "deposits" ? isDeposit : !isDeposit;
  });
